import { AppPage } from '../types';

const APP_BASE_PATH = '';

const pagePathMap: Record<AppPage, string> = {
  tutorial: `/tutorial`,
  ehr: '/ehr',
  'health-records': '/health-records',
  journal: '/journal',
  'eir-chat': '/eir-chat',
  calendar: '/calendar',
  'health-data': '/health-data',
  'health-assessment': '/health-assessment',
  'health-programs': '/health-programs',
  'health-literacy': '/health-literacy',
  'cbt-programs': '/cbt-programs',
  'health-map': '/health-map',
  'health-videos': '/health-videos',
  'health-actions': '/health-actions',
  nutrition: '/nutrition',
  'care-coach': '/care-coach',
  'request-care': '/request-care',
  feedback: '/feedback',
  'personal-growth': '/personal-growth',
  'get-care': '/get-care',
  'eir-docs': '/eir-docs',
  documentation: '/documentation',
  roadmap: '/roadmap',
  'for-you': '/for-you',
  privacy: '/privacy',
  skills: '/skills',
  'my-health': '/my-health',
  'my-health-mental': '/my-health/mental',
  'my-health-physical': '/my-health/physical',
  'my-health-emotional': '/my-health/emotional',
  'my-health-social': '/my-health/social',
  tracking: '/tracking',
};

const inversePagePathMap: Record<string, AppPage> = Object.entries(pagePathMap).reduce(
  (accumulator, [page, path]) => {
    accumulator[path] = page as AppPage;
    return accumulator;
  },
  {} as Record<string, AppPage>
);

export const forYouSubPaths = {
  'your-day': '/for-you/your-day',
  saved: '/for-you/saved',
  history: '/for-you/history',
} as const;

export const healthAssessmentSubPaths = {
  tests: '/health-assessment/tests',
  ai: '/health-assessment/ai',
  results: '/health-assessment/results',
} as const;

const normalizePath = (path: string): string => {
  if (!path) {
    return '/';
  }

  const trimmed = path.split('#')[0]?.split('?')[0] ?? path;
  const normalized = trimmed.replace(/\/+$/, '');
  return normalized || '/';
};

export const getPathForPage = (page: AppPage, subpage?: string): string => {
  const basePath = pagePathMap[page];
  if (!subpage) return basePath;
  
  // Add subpage as URL parameter
  let paramName = 'program';
  if (page === 'personal-growth' || page === 'ehr' || page === 'journal') {
    paramName = 'tab';
  }
  const separator = basePath.includes('?') ? '&' : '?';
  return `${basePath}${separator}${paramName}=${encodeURIComponent(subpage)}`;
};

export const resolvePageFromPath = (path: string): AppPage => {
  const normalized = normalizePath(path);
  if (normalized.startsWith('/my-health/mental')) return 'my-health-mental';
  if (normalized.startsWith('/my-health/physical')) return 'my-health-physical';
  if (normalized.startsWith('/my-health/emotional')) return 'my-health-emotional';
  if (normalized.startsWith('/my-health/social')) return 'my-health-social';
  if (normalized === '/my-health') return 'my-health';
  if (normalized.startsWith('/for-you')) return 'for-you';
  if (normalized.startsWith('/health-assessment')) return 'health-assessment';
  if (normalized === '/health-records') return 'health-records';
  if (normalized === '/tracking') return 'tracking';
  return inversePagePathMap[normalized] ?? 'eir-chat';
};

export const pageTitleMap: Record<AppPage, string> = {
  ehr: 'Health Records',
  'health-records': 'Health Records',
  journal: 'Journal',
  'eir-chat': 'Eir Chat',
  calendar: 'Calendar',
  'health-data': 'Health Data',
  'health-assessment': 'Health Assessment',
  'health-programs': 'Health Programs',
  'health-literacy': 'Health Literacy',
  'cbt-programs': 'CBT Programs',
  'health-map': 'Health Map',
  'health-videos': 'Health Videos',
  'health-actions': 'Health Actions',
  'care-coach': 'Care Coach',
  nutrition: 'Nutrition',
  'request-care': 'Request Care',
  feedback: 'Feedback',
  'personal-growth': 'Personal Growth',
  'get-care': 'Get Care',
  tutorial: 'Tutorial',
  'eir-docs': 'Eir Docs',
  documentation: 'Documentation',
  roadmap: 'Roadmap',
  'for-you': 'For You',
  privacy: 'Privacy',
  skills: 'Skills',
  'my-health': 'My Health',
  'my-health-mental': 'My Mental Health',
  'my-health-physical': 'My Physical Health',
  'my-health-emotional': 'My Emotional Health',
  'my-health-social': 'My Social Health',
  tracking: 'Tracking',
};

export const appPagePaths = pagePathMap;
export { APP_BASE_PATH };
